import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPost, apiDelete } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import { Section } from '../../../components/ui/Section';

function monthStr(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function nextMonthStr(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo, 1);
  return monthStr(d.getFullYear(), d.getMonth() + 1);
}

function prevMonthStr(m: string): string {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 2, 1);
  return monthStr(d.getFullYear(), d.getMonth() + 1);
}

interface MonthGridProps {
  month: string;
  unavailSet: Set<string>;
  pendingSet: Set<string>;
  bulkMode: boolean;
  toggling: string | null;
  onToggle: (dateStr: string) => void;
}

function MonthGrid({ month, unavailSet, pendingSet, bulkMode, toggling, onToggle }: MonthGridProps) {
  const [year, monthNum] = month.split('-').map(Number);
  const daysInMonth = new Date(year, monthNum, 0).getDate();
  const firstDow = (new Date(year, monthNum - 1, 1).getDay() + 6) % 7;
  const label = new Date(year, monthNum - 1).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

  return (
    <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <p style={{ textAlign: 'center', fontWeight: 600, fontSize: 'var(--text-sm)', textTransform: 'capitalize', marginBottom: '0.25rem' }}>
        {label}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
        {['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.65rem', color: 'var(--color-text-faint)', padding: '0.2rem 0', fontWeight: 600 }}>
            {d}
          </div>
        ))}
        {Array.from({ length: firstDow }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
          const dateStr = `${year}-${String(monthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isUnavail = unavailSet.has(dateStr);
          const isPending = pendingSet.has(dateStr);
          const isLoading = toggling === dateStr;

          let bg = 'var(--glass-bg-card)';
          let color = 'var(--color-text)';
          let border = 'rgba(255,255,255,0.10)';
          if (isUnavail && !isPending) { bg = 'var(--color-error, #ef4444)'; color = '#fff'; border = 'var(--color-error, #ef4444)'; }
          if (isPending && !isUnavail) { bg = '#f59e0b22'; color = 'var(--color-text)'; border = '#f59e0b'; }
          if (isPending && isUnavail) { bg = '#f59e0b22'; color = 'var(--color-text)'; border = '#f59e0b'; }

          return (
            <button
              key={day}
              onClick={() => !isLoading && onToggle(dateStr)}
              title={bulkMode
                ? (isPending ? 'Deseleziona' : (isUnavail ? 'In coda per rimozione' : 'Seleziona per bloccare'))
                : (isUnavail ? `${dateStr} — Non disponibile` : `${dateStr} — Disponibile`)
              }
              style={{
                padding: '0.4rem 0.2rem',
                background: bg, color,
                border: `1px solid ${border}`,
                borderRadius: 'var(--radius-interactive)',
                cursor: isLoading ? 'wait' : 'pointer',
                fontSize: '0.75rem',
                fontWeight: (isUnavail || isPending) ? 600 : 400,
                opacity: isLoading ? 0.5 : 1,
                transition: 'all 0.1s',
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AvailabilityCalendar({ collaboratorId, collaboratorName }: { collaboratorId: string; collaboratorName: string }) {
  const qc = useQueryClient();

  const [currentMonth, setCurrentMonth] = useState<string>(() => {
    const now = new Date();
    return monthStr(now.getFullYear(), now.getMonth() + 1);
  });
  const secondMonth = nextMonthStr(currentMonth);

  const [toggling, setToggling] = useState<string | null>(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [pendingSet, setPendingSet] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const fetchUnavail = (month: string) =>
    apiGet<{ dates: string[] }>(`/collaborators/${collaboratorId}/unavailability?month=${month}`);

  const { data: unavailData1 } = useQuery<{ dates: string[] }>({
    queryKey: ['unavailability', collaboratorId, currentMonth],
    queryFn: () => fetchUnavail(currentMonth),
  });
  const { data: unavailData2 } = useQuery<{ dates: string[] }>({
    queryKey: ['unavailability', collaboratorId, secondMonth],
    queryFn: () => fetchUnavail(secondMonth),
  });

  const unavailSet = new Set([...(unavailData1?.dates ?? []), ...(unavailData2?.dates ?? [])]);

  const handleToggle = async (dateStr: string) => {
    if (bulkMode) {
      setPendingSet(prev => {
        const next = new Set(prev);
        if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
        return next;
      });
      return;
    }
    setToggling(dateStr);
    try {
      if (unavailSet.has(dateStr)) {
        await apiDelete(`/collaborators/${collaboratorId}/unavailability/${dateStr}`);
      } else {
        await apiPost(`/collaborators/${collaboratorId}/unavailability`, { dates: [dateStr] });
      }
      qc.invalidateQueries({ queryKey: ['unavailability', collaboratorId] });
    } catch (e: unknown) {
      toast.error((e as Error).message ?? 'Errore aggiornamento disponibilità');
    } finally {
      setToggling(null);
    }
  };

  const applyBulk = async () => {
    if (!pendingSet.size) return;
    setSaving(true);
    try {
      const toAdd = Array.from(pendingSet).filter(d => !unavailSet.has(d));
      const toRemove = Array.from(pendingSet).filter(d => unavailSet.has(d));
      await Promise.all([
        ...(toAdd.length ? [apiPost(`/collaborators/${collaboratorId}/unavailability`, { dates: toAdd })] : []),
        ...toRemove.map(d => apiDelete(`/collaborators/${collaboratorId}/unavailability/${d}`)),
      ]);
      qc.invalidateQueries({ queryKey: ['unavailability', collaboratorId] });
      toast.success(`${toAdd.length} giorni bloccati, ${toRemove.length} sbloccati`);
    } catch (e: unknown) {
      toast.error((e as Error).message ?? 'Errore salvataggio');
    } finally {
      setSaving(false);
      setPendingSet(new Set());
      setBulkMode(false);
    }
  };

  const cancelBulk = () => { setPendingSet(new Set()); setBulkMode(false); };
  const totalUnavail = unavailSet.size;

  void collaboratorName;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
        Segna i giorni in cui sei <strong>non disponibile</strong> per eventi.
        Quando un admin assegna collaboratori, i tuoi giorni bloccati appaiono in evidenza.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => setCurrentMonth(prevMonthStr(currentMonth))}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', padding: '0.25rem 0.6rem', cursor: 'pointer', color: 'var(--color-text)' }}
        >
          ←
        </button>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 'var(--text-sm)', textAlign: 'center' }} />
        <button
          onClick={() => setCurrentMonth(nextMonthStr(currentMonth))}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', padding: '0.25rem 0.6rem', cursor: 'pointer', color: 'var(--color-text)' }}
        >
          →
        </button>
        <button
          onClick={() => { if (bulkMode) cancelBulk(); else setBulkMode(true); }}
          style={{
            background: bulkMode ? 'var(--color-primary)' : 'var(--glass-bg-card)',
            color: bulkMode ? '#fff' : 'var(--color-text)',
            border: `1px solid ${bulkMode ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
            borderRadius: 'var(--radius-interactive)',
            padding: '0.25rem 0.75rem',
            cursor: 'pointer',
            fontSize: 'var(--text-xs)',
            fontWeight: 500,
          }}
        >
          {bulkMode ? 'Annulla selezione' : 'Selezione multipla'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <MonthGrid month={currentMonth} unavailSet={unavailSet} pendingSet={pendingSet} bulkMode={bulkMode} toggling={toggling} onToggle={handleToggle} />
        <MonthGrid month={secondMonth} unavailSet={unavailSet} pendingSet={pendingSet} bulkMode={bulkMode} toggling={toggling} onToggle={handleToggle} />
      </div>

      {bulkMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', border: '1px solid rgba(255,255,255,0.10)' }}>
          <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {pendingSet.size > 0
              ? `${pendingSet.size} giorn${pendingSet.size === 1 ? 'o' : 'i'} selezionat${pendingSet.size === 1 ? 'o' : 'i'}`
              : 'Clicca sui giorni per selezionarli'}
          </span>
          <Button size="sm" onClick={applyBulk} loading={saving} disabled={!pendingSet.size}>
            Salva selezione
          </Button>
        </div>
      )}

      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', lineHeight: 1.4 }}>
        {totalUnavail > 0
          ? `${totalUnavail} giorn${totalUnavail === 1 ? 'o' : 'i'} bloccati nei prossimi 2 mesi`
          : 'Nessun giorno bloccato · Clicca per segnare giorni non disponibili'}
      </p>
    </div>
  );
}

export default function AvailabilitySection() {
  const { data: myCollab, isLoading, isError } = useQuery<{ id: string; name: string; email: string }>({
    queryKey: ['collaborator-me'],
    queryFn: () => apiGet<{ id: string; name: string; email: string }>('/collaborators/me'),
    retry: false,
  });

  return (
    <Section title="Disponibilità personale">
      {isLoading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento…</p>
      ) : isError || !myCollab ? (
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <p>Nessun profilo collaboratore associato alla tua email.</p>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
            Per gestire la disponibilità, chiedi all'admin di creare un profilo collaboratore con la tua email.
          </p>
        </div>
      ) : (
        <AvailabilityCalendar collaboratorId={myCollab.id} collaboratorName={myCollab.name} />
      )}
    </Section>
  );
}
